// Sugalarm — History chart legend popover (cycle 44; cycle 45 variant).
//
// The History chart draws five distinct marker glyphs with no on-screen
// key. This popover explains them. It is STATIC — it always shows the
// full set regardless of what the active window currently contains, so
// the meaning of the glyphs is consistent across every visit.
//
// Two presentation modes (cycle 45):
//   - "anchored" (default, desktop): the cycle-44 behavior — a small
//     popover anchored under the header info button, dismissed by
//     mousedown-outside / Escape / "Got it". Unchanged from cycle 44.
//   - "centered" (mobile + kiosk): a backdrop-dimmed, viewport-centered
//     modal-style card with the cycle-43 scale-in motion, dismissed by
//     backdrop tap / Escape / "Got it". Fixes the cycle-44 left-edge
//     clip on phone viewports where the right-anchored popover ran off
//     screen.
//
// The five rows + "Got it" button render identically in both modes —
// only the chrome differs. Glyphs come from the shared markerGlyphs
// helper (the same primitives HistoryChart draws) so the legend can
// never lie about the chart.

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { MarkerGlyph, type MarkerKind, treatmentColor } from "./markerGlyphs";

interface Props {
  /** Only consumed in "anchored" mode; optional so "centered" callers
   *  needn't thread a ref. */
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  /** Default "anchored" keeps every pre-cycle-45 caller working. */
  mode?: "anchored" | "centered";
}

interface Row {
  kind: MarkerKind;
  color: string;
  label: string;
  sub: string;
}

// Representative colors. carbs / insulin / other pull the *exact*
// treatmentColor() the chart uses for those event types so the two
// surfaces can't drift; reading uses ok-green as the neutral stand-in
// for the zone-colored dots; alert uses urgent-red (the sub line
// explains the warn-orange variant).
const ROWS: Row[] = [
  {
    kind: "reading",
    color: "var(--zone-ok)",
    label: "Glucose reading",
    sub: "One CGM value. Color shows zone.",
  },
  {
    kind: "carbs",
    color: treatmentColor("carbs"),
    label: "Carbs",
    sub: "Meal or snack logged.",
  },
  {
    kind: "insulin",
    color: treatmentColor("insulin"),
    label: "Insulin",
    sub: "Bolus or correction logged.",
  },
  {
    kind: "other",
    color: "var(--ink-2)",
    label: "Other event",
    sub: "Note, exercise, manual BG check, etc.",
  },
  {
    kind: "alert",
    color: "var(--zone-urgent)",
    label: "Alert",
    sub: "Threshold crossed. Color: red urgent, orange warn.",
  },
];

interface Pos {
  top: number;
  left?: number;
  right?: number;
}

export function HistoryLegend({
  anchorEl = null,
  onClose,
  mode = "anchored",
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Pos>({ top: -9999, left: -9999 });
  // Anchored mode renders immediately (no entrance motion, cycle-44
  // behavior). Centered mode runs the cycle-43 modal scale-in.
  const [shown, setShown] = useState<boolean>(mode !== "centered");

  // Anchored positioning: under the anchor, right-aligned when the
  // anchor sits in the right half of the viewport (mirrors the
  // viewport-half right-flip pattern). Recompute on resize.
  useLayoutEffect(() => {
    if (mode !== "anchored" || !anchorEl) return;
    const place = () => {
      const r = anchorEl.getBoundingClientRect();
      const top = r.bottom + 6;
      const center = r.left + r.width / 2;
      if (center > window.innerWidth / 2) {
        setPos({ top, right: Math.max(12, window.innerWidth - r.right) });
      } else {
        setPos({ top, left: Math.max(12, r.left) });
      }
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [anchorEl, mode]);

  // Centered mode scale-in: flip `shown` on the next frame so the
  // transform/opacity transition actually animates from the closed
  // state. Same motion family as the cycle-43 ActiveBolusesModal.
  useEffect(() => {
    if (mode !== "centered") return;
    const id = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(id);
  }, [mode]);

  // Escape closes in both modes. Mousedown-outside is anchored-only —
  // centered mode dismisses via the backdrop's own onClick, so there's
  // no anchor element to exclude.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    let onDown: ((e: MouseEvent) => void) | null = null;
    if (mode === "anchored") {
      onDown = (e: MouseEvent) => {
        const t = e.target;
        if (!(t instanceof Node)) return;
        if (ref.current?.contains(t)) return;
        // Let the anchor (info button) handle its own toggle click —
        // otherwise mousedown would close and the click would re-open.
        if (anchorEl?.contains(t)) return;
        onClose();
      };
      window.addEventListener("mousedown", onDown);
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      if (onDown) window.removeEventListener("mousedown", onDown);
    };
  }, [onClose, anchorEl, mode]);

  const content = (
    <>
      <div
        style={{
          fontSize: "var(--type-floor)",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--ink-3)",
        }}
      >
        Chart legend
      </div>

      {ROWS.map((row) => (
        <div
          key={row.kind}
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <MarkerGlyph kind={row.kind} color={row.color} wrap size={16} />
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "var(--ink)",
                lineHeight: 1.25,
              }}
            >
              {row.label}
            </div>
            <div
              style={{
                fontSize: "var(--type-floor)",
                color: "var(--ink-3)",
                lineHeight: 1.35,
              }}
            >
              {row.sub}
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={onClose}
        style={{
          appearance: "none",
          alignSelf: "flex-end",
          border: "0.5px solid var(--line)",
          background: "transparent",
          color: "var(--ink-2)",
          borderRadius: 999,
          padding: "6px 12px",
          fontSize: "var(--type-floor)",
          fontWeight: 600,
          minHeight: 28,
          cursor: "pointer",
        }}
      >
        Got it
      </button>
    </>
  );

  if (mode === "centered") {
    return (
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "rgba(0, 0, 0, 0.32)",
          backdropFilter: "blur(3px)",
          transition: "background 220ms ease-out",
          padding: 16,
        }}
      >
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          aria-label="Chart legend"
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "min(360px, calc(100vw - 32px))",
            maxHeight: "calc(100vh - 64px)",
            overflowY: "auto",
            padding: "12px 14px",
            background: "var(--bg-elev)",
            border: "0.5px solid var(--line-strong)",
            borderRadius: 14,
            boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            transform: shown
              ? "scale(1) translateY(0)"
              : "scale(0.94) translateY(8px)",
            opacity: shown ? 1 : 0,
            transition: "transform 220ms ease-out, opacity 200ms ease-out",
          }}
        >
          {content}
        </div>
      </div>
    );
  }

  // Anchored mode — cycle-44 behavior, unchanged.
  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Chart legend"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        right: pos.right,
        width: "min(320px, calc(100vw - 24px))",
        padding: "12px 14px",
        background: "var(--bg-elev)",
        border: "0.5px solid var(--line-strong)",
        borderRadius: 14,
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
        display: "flex",
        flexDirection: "column",
        gap: 10,
        zIndex: 50,
      }}
    >
      {content}
    </div>
  );
}
